import { isTulipShape } from './tulip'
import type { Tulip, TulipShape } from './types'

/**
 * Woran ein Vorschlag wiedererkannt wird - und wo ein Nein festgehalten wird.
 *
 * Das Problem, das dieses Modul löst: die Vorschläge werden mehrfach geholt.
 * Nach jeder Streckenänderung, nach jedem Belagsabruf, und auf einer Reise mit
 * sechzehn Etappen und teils über 120 Manövern je Etappe auch einfach noch
 * einmal. Ohne ein Gedächtnis kommt dabei jedes Mal dieselbe Liste heraus,
 * vollständig angehakt - auch die Zeile, die jemand vorgestern begründet
 * weggeworfen hat, und auch die, die längst im Buch steht. Zweimal übernehmen
 * ergab dann zwei gleiche Zeilen.
 *
 * Und eine doppelte Tulpe im gedruckten Buch ist eine Anweisung, die es zweimal
 * gibt. Im Fahrzeug bei Tempo 80 gelesen ist das schlimmer als eine fehlende:
 * eine fehlende merkt man an der Kreuzung, eine doppelte lässt einen die
 * nächste Kreuzung für die übernächste halten.
 *
 * Die Antwort besteht aus zwei Teilen, und beide stehen im Dokument und nicht
 * in der Maske:
 *
 *  - Was aus einem Vorschlag übernommen wurde, trägt dessen Stelle bei sich
 *    (Tulip.src). Daran erkennt der nächste Lauf, dass diese Zeile schon im
 *    Buch steht.
 *  - Was abgelehnt wurde, steht als Stelle an der Etappe (Stage.tulipsRejected).
 *    Sonst bliebe von einem Nein nichts übrig - das Abwählen hinterlässt ja
 *    gerade keine Tulpe.
 *
 * Am Dokument, weil die Reise geteilt ist: ein Nein, das nur in der Maske des
 * einen Geräts steht, ist auf dem zweiten keines.
 *
 * Reine Funktionen über Listen, kein React: die Regeln lassen sich damit
 * prüfen (tulipMerge.test.ts), und bei einer Auswahl, die man nicht ansieht,
 * ist das die einzige Art, sie zu verantworten.
 */

/**
 * Die Stelle, die ein Vorschlag meint: das Zeichen und der Kilometerstand.
 *
 * Ausdrücklich ohne den Hinweistext und ohne die Richtung. Beides kann sich
 * ändern, ohne dass es eine andere Stelle wäre: der Wegweiser einer neu
 * gerechneten Strecke heisst mal "Zadar" und mal "Zadar - zapad", und an die
 * Notiz einer Tunnelzeile hängt die Übernahme den Vorbehalt über die
 * Kartendeckung an (mitAbdeckungsvermerk). Was bleibt, ist die Frage "welches
 * Zeichen an welcher Stelle der Etappe" - und genau die beantwortet ein
 * Vorschlag.
 */
export interface Stelle {
  shape: TulipShape
  /** Kilometerstand ab Beginn der Etappe. */
  km: number
}

/**
 * Wie weit zwei Kilometerstände auseinanderliegen dürfen, um dieselbe Stelle
 * zu meinen.
 *
 * Hundertfünfzig Meter, und die Zahl ist von NAHE_M in tulipSuggest.ts
 * abgeschrieben - dort ist es der Abstand, unter dem zwei Vorschläge zu einem
 * zusammengelegt werden. Dieselbe Frage, dieselbe Antwort: zwei verschiedene
 * Zahlen für "dieselbe Stelle" wären zwei Wahrheiten.
 *
 * Dass es dieselbe Zahl ist, ist zugleich die Sicherung. Die Vorschläge eines
 * Laufes sind nach dem Zusammenlegen mindestens so weit auseinander; eine
 * Wiedererkennung, die enger misst, kann deshalb niemals zwei verschiedene
 * Vorschläge verwechseln und damit auch keine Zeile verschlucken. Nachgemessen
 * an den sieben Antwortdateien des Routendienstes: das engste Paar gleicher
 * Form liegt 293 Meter auseinander (Barcelona - Palma, zwei Kreuzungen bei
 * 262.51 und 262.803).
 *
 * Ein Kilometer - TULPE_TOLERANZ_KM in navexport.ts - wäre hier falsch. Dort
 * geht es um den Vergleich eines abgelesenen Tachostands mit der Rechnung des
 * Dienstes am Ende einer Etappe, und über 200 Kilometer summiert sich das. Hier
 * werden zwei Rechnungen desselben Dienstes über dieselbe Strasse verglichen,
 * und im Ortskern stehen zwei Kreisel keinen Kilometer auseinander.
 */
export const STELLE_TOLERANZ_KM = 0.15

/**
 * Wie viele Absagen eine Etappe mit sich führt.
 *
 * Hundert. Eine Etappe bekommt höchstens zwanzig Vorschläge (MAX_VORSCHLAEGE),
 * die Liste fasst also fünf vollständig verschiedene Fassungen der Strecke.
 * Wer darüber hinaus arbeitet, verliert seine ältesten Absagen - die zu
 * Strassen, die seit fünf Umplanungen nicht mehr befahren werden.
 *
 * Ein Deckel und nicht "für immer", weil die Liste sonst mit jeder verworfenen
 * Streckenfassung wächst, durch jeden Abgleich geht und in jeder Exportdatei
 * steht, ohne je wieder etwas zu beantworten.
 */
export const MAX_ABSAGEN = 100

/** Was ein Vorschlag für diese Etappe ist. */
export type Lage =
  /** Noch nie entschieden: wird angeboten und ist angehakt. */
  | 'neu'
  /** Steht schon im Roadbook - wird gar nicht mehr angeboten. */
  | 'drin'
  /** Schon einmal abgelehnt: wird angeboten, aber ohne Haken. */
  | 'nein'

/** Das Zeichen zwischen Form und Kilometerstand im Schlüssel. */
const TRENNER = '@'

/**
 * Der Schlüssel einer Stelle, wie er im Dokument steht.
 *
 * Eine Zeichenkette und kein Objekt, weil das Feld an zwei Stellen liegt, an
 * denen ein Objekt nur Ballast wäre: an jeder übernommenen Tulpe und in einer
 * Liste von Absagen. "kreisel@3.7" ist dabei lesbar - wer sich eine
 * Exportdatei ansieht, soll erkennen, wovon die Rede ist.
 *
 * Auf drei Nachkommastellen gerundet, weil die Vorschläge auf Meter genau
 * rechnen (kilometer() in tulipSuggest.ts) und eine Fliesskommazahl sonst als
 * "3.7000000000000004" in der Datei stünde.
 *
 * @returns Leer, wenn die Stelle keinen brauchbaren Kilometerstand hat. Eine
 *   Tulpe ohne Kilometerstand ist ein völlig normaler Fall - sie ist unterwegs
 *   notiert worden -, aber sie bezeichnet keine Stelle, die man wiederfinden
 *   könnte.
 */
export function stelleSchluessel(stelle: Stelle): string {
  const { km } = stelle
  if (!Number.isFinite(km) || km < 0) return ''
  return `${stelle.shape}${TRENNER}${String(Math.round(km * 1000) / 1000)}`
}

/**
 * Die Stelle zu einem Schlüssel - oder nichts.
 *
 * Nichts bei allem, was nicht als Stelle zu lesen ist: eine unbekannte Form,
 * ein fehlender Kilometerstand, eine Zeichenkette aus einer fremden Datei. Ein
 * Schlüssel, der sich nicht deuten lässt, darf keine Zeile aus dem Buch halten
 * und auch keine hineinlassen - er wird schlicht übergangen.
 */
export function stelleAus(schluessel: string): Stelle | null {
  const stelle = schluessel.indexOf(TRENNER)
  if (stelle < 0) return null

  const shape = schluessel.slice(0, stelle)
  if (!isTulipShape(shape)) return null

  const km = Number(schluessel.slice(stelle + 1))
  if (!Number.isFinite(km) || km < 0) return null
  return { shape, km }
}

/**
 * Die Stelle, für die eine Tulpe im Buch steht.
 *
 * Bei einer übernommenen Tulpe die des Vorschlags (src) und nicht ihr eigener
 * Kilometerstand. Das ist der Kern der Sache: wer die Zahl von Hand berichtigt
 * - weil der Tacho unterwegs etwas anderes sagte -, hat damit keine andere
 * Stelle gemeint, sondern dieselbe genauer. Am eigenen Kilometerstand gemessen
 * fiele sie beim nächsten Lauf aus der Wiedererkennung, und der Vorschlag käme
 * ein zweites Mal ins Buch.
 *
 * Bei einer von Hand geschriebenen Tulpe ihr Kilometerstand. Auch sie belegt
 * ihre Stelle: wer den Kreisel bei 12.4 selbst eingetragen hat, braucht ihn
 * nicht noch einmal vorgeschlagen.
 */
export function tulipStelle(tulpe: Tulip): Stelle | null {
  const ausVorschlag = tulpe.src ? stelleAus(tulpe.src) : null
  if (ausVorschlag) return ausVorschlag
  if (!Number.isFinite(tulpe.km) || tulpe.km < 0) return null
  return { shape: tulpe.shape, km: tulpe.km }
}

/** Meinen diese beiden Stellen dieselbe? */
function passt(a: Stelle, b: Stelle): boolean {
  return a.shape === b.shape && Math.abs(a.km - b.km) < STELLE_TOLERANZ_KM
}

/**
 * Ordnet jeder Stelle links höchstens eine Stelle rechts zu, und jede rechts
 * nur einmal.
 *
 * Die Eindeutigkeit ist keine Förmlichkeit. Auf einer Alpenstrasse folgen zwei
 * Tunnel hundert Meter aufeinander; beide sind im Buch, beide werden wieder
 * vorgeschlagen. Ohne die Eindeutigkeit belegte der erste Tunnel beide
 * Vorschläge, der zweite Vorschlag fände nichts mehr - und eine Zeile fiele
 * still weg. Genommen wird deshalb je Stelle die nächstliegende noch freie,
 * und bei gleichem Abstand die frühere.
 *
 * @param links Wonach gesucht wird, meist die Vorschläge eines Laufes.
 * @param rechts Worin gesucht wird. Einträge, die keine Stelle bezeichnen,
 *   stehen als null darin und passen zu nichts - so bleiben die Stellen an
 *   ihren Plätzen, und der Aufrufer kann mit dem Index in seine eigene Liste
 *   zurückgehen.
 * @returns Je Eintrag links der Index rechts, oder -1.
 */
export function stellenZuordnung(
  links: readonly (Stelle | null)[],
  rechts: readonly (Stelle | null)[],
): number[] {
  const vergeben = rechts.map(() => false)

  return links.map((suche) => {
    if (!suche) return -1

    let treffer = -1
    let abstand = Number.POSITIVE_INFINITY
    for (const [i, kandidat] of rechts.entries()) {
      if (!kandidat || vergeben[i] || !passt(suche, kandidat)) continue
      const d = Math.abs(kandidat.km - suche.km)
      if (d >= abstand) continue
      treffer = i
      abstand = d
    }
    if (treffer >= 0) vergeben[treffer] = true
    return treffer
  })
}

/**
 * Was von den Vorschlägen eines Laufes noch zu entscheiden ist.
 *
 * @param vorschlaege Die Vorschläge in Fahrreihenfolge.
 * @param tulpen Die Tulpen der Etappe.
 * @param abgesagt Die gemerkten Absagen der Etappe (Stage.tulipsRejected).
 * @returns Je Vorschlag seine Lage, in derselben Reihenfolge.
 */
export function vorschlagsLage(
  vorschlaege: readonly Stelle[],
  tulpen: readonly Tulip[],
  abgesagt: readonly string[],
): Lage[] {
  // Erst das Buch, dann die Absagen. Wer eine abgelehnte Zeile später doch von
  // Hand einträgt, hat sie im Buch - und "steht schon drin" ist dann die
  // Auskunft, die zählt.
  const imBuch = stellenZuordnung(vorschlaege, tulpen.map(tulipStelle))
  const abgelehnt = stellenZuordnung(vorschlaege, abgesagt.map(stelleAus))

  return vorschlaege.map((_, i) => {
    if ((imBuch[i] ?? -1) >= 0) return 'drin'
    return (abgelehnt[i] ?? -1) >= 0 ? 'nein' : 'neu'
  })
}

/**
 * Merkt sich das Nein zu einer Stelle.
 *
 * Steht zu dieser Stelle schon eine Absage, bleibt die Liste, wie sie ist:
 * zweimal Nein ist einmal Nein, und eine Liste, die bei jedem Antippen wächst,
 * reisst irgendwann den Deckel.
 */
export function mitAbsage(abgesagt: readonly string[], stelle: Stelle): string[] {
  const schluessel = stelleSchluessel(stelle)
  if (!schluessel) return [...abgesagt]
  if (abgesagt.some((eintrag) => trifft(eintrag, stelle))) return [...abgesagt]

  // Von hinten gedeckelt: was zuletzt abgelehnt wurde, ist das, was gerade
  // bearbeitet wird.
  return [...abgesagt, schluessel].slice(-MAX_ABSAGEN)
}

/**
 * Nimmt das Nein zu einer Stelle wieder heraus.
 *
 * Es muss zurückzunehmen sein, sonst wäre ein Fehlgriff endgültig: ein Haken,
 * den man am Strassenrand versehentlich wegtippt, kostete sonst eine Zeile für
 * immer, und die einzige Abhilfe wäre, sie von Hand nachzubauen.
 */
export function ohneAbsage(abgesagt: readonly string[], stelle: Stelle): string[] {
  return abgesagt.filter((eintrag) => !trifft(eintrag, stelle))
}

/** Meint dieser gemerkte Schlüssel diese Stelle? */
function trifft(schluessel: string, stelle: Stelle): boolean {
  const gemerkt = stelleAus(schluessel)
  return gemerkt !== null && passt(gemerkt, stelle)
}
